import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import DrawerConfirm from '~/Common-Objects/UI/Drawer/DrawerConfirm'
import DropdownMenu from '~/Common-Objects/UI/DropdownMenu'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import axios from '~/data/http/axios'
import i18n from '~/i18n'
import * as siteActions from '~/data/actions/siteActions'
import { MAILCHIMP_LOGO } from '~/pages/cp/company/integrations/apps/mailchimp2/icons'
import { MAILCHIMP_APP_ID } from '~/utils/mailchimpOAuth'
import { captureMailchimpImportInteracted } from '~/utils/mailchimpImportAnalytics'
import { MAILCHIMP_FLOW_PATH } from './paths'
import { isCompletedMailchimpImport, mailchimpImportAgainPath } from './mailchimpImportStatus'
import { openMailchimpFlow } from './mailchimpFlowOrigin'

/**
 * @param {{ active?: boolean }} props — when true (flow closed), re-check Mailchimp import status
 */
export default function ImportLanding({ active = true }) {
    const history = useHistory()
    const dispatch = useDispatch()
    const requestId = useRef(0)
    const [checking, setChecking] = useState(true)
    const [complete, setComplete] = useState(false)
    const [accountApplication, setAccountApplication] = useState(null)

    useEffect(() => {
        if (!active) return undefined
        const id = ++requestId.current
        setChecking(true)
        axios
            .get('/application/account/app/' + MAILCHIMP_APP_ID)
            .then((res) => {
                if (id !== requestId.current) return
                if (res.data && res.data.error) {
                    setAccountApplication(null)
                    setComplete(false)
                } else {
                    const application = res.data && res.data.Application
                    setAccountApplication(application || null)
                    setComplete(isCompletedMailchimpImport(application))
                }
            })
            .catch(() => {
                if (id !== requestId.current) return
                setAccountApplication(null)
                setComplete(false)
            })
            .finally(() => {
                if (id === requestId.current) setChecking(false)
            })
        return () => {
            requestId.current += 1
        }
    }, [active])

    const disconnect = () => {
        if (!accountApplication || !accountApplication.id) return
        captureMailchimpImportInteracted('disconnect')
        const content = (
            <DrawerConfirm
                header={i18n.t('mailchimp2:disconnect.header')}
                subheader={i18n.t('mailchimp2:disconnect.body')}
                showCancel
                confirm={async () => {
                    await axios.delete('/application/account/' + accountApplication.id)
                    requestId.current += 1
                    setAccountApplication(null)
                    setComplete(false)
                    setChecking(false)
                }}
            />
        )
        dispatch(siteActions.alterSiteDrawer(true, true, 'right', content, true))
    }

    let actions = (
        <LoadingIcons iconType='2' className='mailchimp2-source__spinner' style={{ width: 22, height: 22 }} />
    )
    if (!checking) {
        actions = complete ? (
            <div className='mailchimp2-source__actions'>
                <Button
                    text={i18n.t('mailchimp2:landing.mailchimp.view')}
                    className='button-sml grey'
                    onClick={() => {
                        captureMailchimpImportInteracted('view')
                        openMailchimpFlow(history, MAILCHIMP_FLOW_PATH)
                    }}
                />
                <Button
                    text={i18n.t('mailchimp2:landing.mailchimp.importAgain')}
                    className='button-sml button-primary'
                    onClick={() => {
                        captureMailchimpImportInteracted('import_again')
                        openMailchimpFlow(history, mailchimpImportAgainPath())
                    }}
                />
            </div>
        ) : (
            <Button
                text={i18n.t('mailchimp2:landing.mailchimp.button')}
                className='button-primary'
                onClick={() => {
                    captureMailchimpImportInteracted('import')
                    openMailchimpFlow(history, MAILCHIMP_FLOW_PATH)
                }}
            />
        )
    }

    const connected = !!accountApplication

    return (
        <div className='mailchimp2-landing'>
            <div className='page-header'>{i18n.t('nav:import')}</div>
            <p className='mailchimp2-landing__lead'>{i18n.t('mailchimp2:landing.lead')}</p>
            <Paper className={connected ? 'mailchimp2-source mailchimp2-source--connected' : 'mailchimp2-source'}>
                {connected && (
                    <div className='mailchimp2-source__menu'>
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger
                                variant='ghost'
                                size='s'
                                iconOnly
                                aria-label={i18n.t('mailchimp2:landing.mailchimp.menu')}
                            >
                                <Icons icon='ellipsis' />
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                                <DropdownMenu.Content align='end'>
                                    <DropdownMenu.Item onSelect={disconnect}>
                                        {i18n.t('mailchimp2:landing.mailchimp.disconnect')}
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                    </div>
                )}
                <img src={MAILCHIMP_LOGO} alt='' className='mailchimp2-source__logo' />
                <div className='mailchimp2-source__copy'>
                    <h2>{i18n.t('mailchimp2:landing.mailchimp.title')}</h2>
                    <p>{i18n.t('mailchimp2:landing.mailchimp.body')}</p>
                </div>
                {actions}
            </Paper>
        </div>
    )
}
